import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  Sparkles, Star, Crown, ArrowRight, Heart, 
  Users, Home, Eye, Moon, Sun, Music, Utensils, 
  Palette, BookOpen, Stethoscope, Wrench, Baby, Gift,
  FileText, Archive, Play
} from 'lucide-react';
import GrantSteampunkEngine from './GrantSteampunkEngine';

const RoomOfMirrors = ({ profileData, onEnterFederation, onNavigateBack, onContinueDialogue }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [mairyTerminal, setMairyTerminal] = useState('');
  const [terminalStep, setTerminalStep] = useState(0);
  const [showConstellation, setShowConstellation] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [userInputs, setUserInputs] = useState([]);
  const [interactiveMode, setInteractiveMode] = useState(false);
  const [spiralMath, setSpiralMath] = useState({
    alpha: 1.618,
    theta: 0,
    intensity: 1.0,
    zoom: 50
  });

  // Calculate spiral parameters from user inputs
  useEffect(() => {
    const thresholdCount = profileData?.selectedThresholds?.length || 1;
    const giftCount = profileData?.selectedGifts?.length || 1;
    const inputCount = userInputs.length || 1;
    
    // Simple mathematical derivation from user data
    const alpha = 1.618 + (thresholdCount * 0.1);
    const theta = (giftCount * 45) % 360;
    const intensity = Math.min(2.0, 1 + (inputCount * 0.2));
    const zoom = Math.min(100, 50 + (thresholdCount + giftCount) * 5);
    
    setSpiralMath({ alpha, theta, intensity, zoom });
  }, [profileData, userInputs]);

  // Super slow motion spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.05) % 360); // Super slow motion
    }, 100); // Slower interval
    return () => clearInterval(spiralInterval);
  }, []);

  // M.ai.ry terminal sequence
  const terminalSequence = [
    "Analyzing your Guild profile...",
    "",
    `Gift Bearer: ${profileData.giftName}`,
    `Thresholds: ${profileData.selectedThresholds?.length || 1} selected`,
    `Gifts: ${profileData.selectedGifts?.length || 1} offered`,
    "",
    "I see you clearly now...",
    "Generating your soul constellation...",
    "",
    "Your family's cosmic signature is emerging...",
    "",
    "[CONSTELLATION READY FOR INTERACTION]"
  ];

  useEffect(() => {
    if (terminalStep < terminalSequence.length) {
      const timer = setTimeout(() => {
        setMairyTerminal(prev => prev + terminalSequence[terminalStep] + '\n');
        setTerminalStep(prev => prev + 1);
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      setTimeout(() => {
        setShowConstellation(true);
        setInteractiveMode(true);
      }, 1000);
    }
  }, [terminalStep]);

  const sendUserInput = () => {
    if (!userInput.trim()) return;
    
    const newInput = {
      content: userInput,
      timestamp: new Date(),
      context: 'soul_reflection'
    };
    setUserInputs(prev => [...prev, newInput]);
    
    // M.ai.ry responds based on input
    const responses = [
      `"${userInput}" - This deepens my understanding of your family's constellation.`,
      `Your authentic expression reveals new dimensions in your Guild profile.`,
      `I'm weaving this insight into your family lore archive.`,
      `This helps me better match you with Federation families.`
    ];
    
    const response = responses[Math.floor(Math.random() * responses.length)];
    setMairyTerminal(prev => prev + `\n> ${userInput}\n< ${response}\n`);
    setUserInput('');
  };

  // Interactive constellation points
  const constellationElements = [
    { 
      id: 'thresholds', 
      label: 'Life Thresholds', 
      position: { x: 25, y: 30 }, 
      icon: Moon,
      color: '#ff6b9d',
      data: profileData.selectedThresholds || []
    },
    { 
      id: 'gifts', 
      label: 'Sacred Gifts', 
      position: { x: 75, y: 25 }, 
      icon: Gift,
      color: '#ffd700',
      data: profileData.selectedGifts || []
    },
    { 
      id: 'path', 
      label: 'Federation Path', 
      position: { x: 50, y: 70 }, 
      icon: Users,
      color: '#00ffff',
      data: [profileData.userType]
    },
    { 
      id: 'essence', 
      label: 'Family Essence', 
      position: { x: 50, y: 15 }, 
      icon: Sparkles,
      color: '#ff1493',
      data: userInputs
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      {/* Dynamic Background */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${profileData.background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Super Zoomed Grant Spiral Mirror Effect */}
      <div className="fixed inset-0 pointer-events-none opacity-40">
        {/* Primary Mirror Spiral - Super Zoomed */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(${spiralMath.zoom}) rotate(${spiralPhase + spiralMath.theta}deg)`,
            filter: 'blur(1px) brightness(2.5) saturate(2.2) contrast(1.3)',
            mixBlendMode: 'color-dodge'
          }}
        >
          <GrantSteampunkEngine 
            size={100}
            alpha={spiralMath.alpha}
            intensity={spiralMath.intensity}
          />
        </div>
        
        {/* Secondary Mirror Layer */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(${spiralMath.zoom * 0.7}) rotate(${-spiralPhase * 0.5 + spiralMath.theta}deg)`,
            filter: 'blur(2px) brightness(2.0) saturate(1.8) hue-rotate(30deg)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantSteampunkEngine 
            size={80}
            alpha={spiralMath.alpha * 1.2}
            intensity={spiralMath.intensity * 0.8}
          />
        </div>
        
        {/* Tertiary Mirror Reflection */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(${spiralMath.zoom * 0.4}) rotate(${spiralPhase * 1.5 - spiralMath.theta}deg)`,
            filter: 'blur(3px) brightness(1.8) saturate(1.5) hue-rotate(-20deg)',
            mixBlendMode: 'soft-light'
          }}
        >
          <GrantSteampunkEngine 
            size={60}
            alpha={spiralMath.alpha * 0.8}
            intensity={spiralMath.intensity * 1.3}
          />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-transparent to-black/60" />

      <div className="relative z-10 flex flex-col min-h-screen p-8">
        
        {/* Room Header */}
        <div className="text-center mb-8">
          <h1 
            className="text-4xl md:text-5xl font-bold text-yellow-200 mb-4"
            style={{
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 40px rgba(255, 215, 0, 0.8)'
            }}
          >
            🪞 The Room of Mirrors
          </h1>
        </div>

        {/* M.ai.ry Terminal Interface - Universal Style */}
        <div className="max-w-4xl mx-auto mb-12">
          <div 
            className="bg-black/95 backdrop-blur-xl border-4 border-pink-500 rounded-lg p-6 transform rotate-1 shadow-2xl"
            style={{
              boxShadow: '0 0 40px #ff1493, inset 0 0 20px #ff149320',
              filter: 'drop-shadow(0 0 20px #ff1493)',
              position: 'relative',
              zIndex: 50
            }}
          >
            <div className="font-mono">
              {/* Terminal Header */}
              <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-pink-500/30">
                <div className="w-3 h-3 bg-pink-500 rounded-full animate-pulse"></div>
                <span className="text-pink-400 text-sm">M.ai.ry@soul-mirror</span>
              </div>
              
              <div 
                className="text-pink-400 leading-relaxed min-h-[120px] text-sm mb-4"
                style={{ 
                  fontFamily: 'Courier New, monospace',
                  textShadow: '0 0 10px #ff1493'
                }}
              >
                <pre className="whitespace-pre-wrap">
                  {mairyTerminal}
                  <span className="animate-pulse">█</span>
                </pre>
              </div>
              
              {/* User Input Box - Separate */}
              {interactiveMode && (
                <div className="border-2 border-pink-400/50 bg-black/50 rounded p-3 mt-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-pink-400 text-xs">soul@mirror:~$</span>
                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && userInput.trim()) {
                          sendUserInput();
                        }
                      }}
                      placeholder="Reflect on your family's journey..."
                      className="flex-1 bg-transparent text-white text-sm outline-none font-mono"
                    />
                    <span className="text-pink-400 animate-pulse">█</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AMAZING INTERACTIVE SOUL CONSTELLATION */}
        {showConstellation && (
          <div className="max-w-6xl mx-auto mb-12 relative h-96">
            <div className="relative h-full bg-black/60 backdrop-blur-xl border-4 border-purple-400/50 rounded-2xl p-8 overflow-hidden">
              
              {/* Interactive Constellation Points */}
              {constellationElements.map((element, index) => {
                const IconComponent = element.icon;
                const delay = index * 0.5;
                
                return (
                  <button
                    key={element.id}
                    onClick={() => {
                      setMairyTerminal(prev => prev + `\n\n> Accessing ${element.label}...\n< ${element.data.length || 1} elements detected\n< Click to explore deeper\n`);
                    }}
                    className="absolute group transition-all duration-1000 hover:scale-125"
                    style={{
                      left: `${element.position.x}%`,
                      top: `${element.position.y}%`,
                      transform: 'translate(-50%, -50%)',
                      animation: `float 3s ease-in-out infinite ${delay}s`
                    }}
                  >
                    <div 
                      className="relative bg-black/80 backdrop-blur-md border-4 rounded-full p-6 group-hover:bg-black/90 transition-all"
                      style={{
                        borderColor: element.color,
                        boxShadow: `0 0 30px ${element.color}80`,
                        animation: 'pulse 2s ease-in-out infinite'
                      }}
                    >
                      <IconComponent 
                        className="w-8 h-8"
                        style={{ color: element.color }}
                      />
                      
                      {/* Data Points Orbiting */}
                      {element.data.slice(0, 3).map((_, dataIndex) => (
                        <div
                          key={dataIndex}
                          className="absolute w-2 h-2 rounded-full"
                          style={{
                            backgroundColor: element.color,
                            animation: `orbit 4s linear infinite ${dataIndex * 1.3}s`,
                            transformOrigin: '0 0'
                          }}
                        />
                      ))}
                      
                      <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                        <span 
                          className="text-sm font-medium px-2 py-1 rounded"
                          style={{ 
                            color: element.color,
                            backgroundColor: `${element.color}20`,
                            border: `1px solid ${element.color}40`
                          }}
                        >
                          {element.label}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
              
              {/* Connecting Energy Lines */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <defs>
                  <linearGradient id="energyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ff1493" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="#ffd700" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#00ffff" stopOpacity="0.8" />
                  </linearGradient>
                  
                  <filter id="energyGlow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Dynamic connecting lines */}
                <path
                  d="M 25% 30% Q 40% 10% 50% 15% Q 60% 10% 75% 25% Q 60% 50% 50% 70% Q 40% 50% 25% 30%"
                  stroke="url(#energyGradient)"
                  strokeWidth="3"
                  fill="none"
                  filter="url(#energyGlow)"
                  className="animate-pulse"
                  style={{
                    animation: `energyFlow 4s ease-in-out infinite`
                  }}
                />
              </svg>
            </div>
          </div>
        )}

        {/* Enhanced Navigation Options - More Graphical */}
        {showConstellation && (
          <div className="max-w-6xl mx-auto text-center space-y-8">
            
            {/* Direction Explanation */}
            <div className="bg-black/70 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
              <h3 className="text-white text-xl font-bold mb-4">Where would you like to go next?</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                
                {/* Continue Dialogue */}
                <div className="bg-purple-900/30 border border-purple-400/30 rounded-xl p-4 hover:bg-purple-900/50 transition-all">
                  <Eye className="w-12 h-12 mx-auto text-purple-300 mb-3" />
                  <h4 className="text-purple-200 font-bold mb-2">Continue Chat</h4>
                  <p className="text-purple-300 text-sm mb-4">Keep talking with M.ai.ry about your family's journey</p>
                  <Button
                    onClick={() => {
                      setInteractiveMode(true);
                      setMairyTerminal(prev => prev + '\n\nM.ai.ry: I\'m here to listen. What else would you like to share about your family?');
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    size="sm"
                  >
                    Continue
                  </Button>
                </div>
                
                {/* Secret History Game - Highlighted Option */}
                <div className="bg-cyan-900/40 border-2 border-cyan-400/50 rounded-xl p-4 hover:bg-cyan-900/60 transition-all relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10"></div>
                  <div className="relative z-10">
                    <Play className="w-12 h-12 mx-auto text-cyan-300 mb-3 animate-pulse" />
                    <h4 className="text-cyan-200 font-bold mb-2">✨ Enter the Game</h4>
                    <p className="text-cyan-300 text-sm mb-4 leading-relaxed">
                      <strong>Elucidate next steps</strong> and find the right teams to pull your holiday together through consciousness gameplay
                    </p>
                    <Button
                      onClick={() => {
                        if (window.handleNavigateToSecretHistory) {
                          window.handleNavigateToSecretHistory();
                        } else {
                          // Navigate to Sacred Tenets to start game
                          onNavigateToSecretHistory && onNavigateToSecretHistory();
                        }
                      }}
                      className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold"
                      size="sm"
                    >
                      🎮 Start Adventure
                    </Button>
                  </div>
                </div>
                
                {/* Guild Haus Dashboard */}
                <div className="bg-yellow-900/30 border border-yellow-400/30 rounded-xl p-4 hover:bg-yellow-900/50 transition-all">
                  <Home className="w-12 h-12 mx-auto text-yellow-300 mb-3" />
                  <h4 className="text-yellow-200 font-bold mb-2">Guild Haus</h4>
                  <p className="text-yellow-300 text-sm mb-4">Your personal Federation dashboard and retreat coordination</p>
                  <Button
                    onClick={onEnterFederation}
                    className="w-full bg-yellow-600 hover:bg-yellow-700"
                    size="sm"
                  >
                    Enter
                  </Button>
                </div>
                
                {/* Family Lore Library */}
                <div className="bg-cyan-900/30 border border-cyan-400/30 rounded-xl p-4 hover:bg-cyan-900/50 transition-all">
                  <BookOpen className="w-12 h-12 mx-auto text-cyan-300 mb-3" />
                  <h4 className="text-cyan-200 font-bold mb-2">Family Archive</h4>
                  <p className="text-cyan-300 text-sm mb-4">Epic museum quality library of your family's journey</p>
                  <Button
                    onClick={() => {
                      if (window.handleNavigateToFamilyLore) {
                        window.handleNavigateToFamilyLore();
                      }
                    }}
                    className="w-full bg-cyan-600 hover:bg-cyan-700"
                    size="sm"
                  >
                    Explore
                  </Button>
                </div>
                
                {/* Project Organizer */}
                <div className="bg-green-900/30 border border-green-400/30 rounded-xl p-4 hover:bg-green-900/50 transition-all">
                  <FileText className="w-12 h-12 mx-auto text-green-300 mb-3" />
                  <h4 className="text-green-200 font-bold mb-2">Project Organizer</h4>
                  <p className="text-green-300 text-sm mb-4">Advanced family project management and collaboration tools</p>
                  <Button
                    onClick={() => {
                      if (window.handleNavigateToMyProjects) {
                        window.handleNavigateToMyProjects();
                      }
                    }}
                    className="w-full bg-green-600 hover:bg-green-700"
                    size="sm"
                  >
                    Organize
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CSS for Amazing Animations */}
        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translate(-50%, -50%) translateY(0px); }
            50% { transform: translate(-50%, -50%) translateY(-10px); }
          }
          
          @keyframes orbit {
            0% { transform: rotate(0deg) translateX(40px) rotate(0deg); }
            100% { transform: rotate(360deg) translateX(40px) rotate(-360deg); }
          }
          
          @keyframes energyFlow {
            0% { stroke-dasharray: 0 100; }
            50% { stroke-dasharray: 50 50; }
            100% { stroke-dasharray: 100 0; }
          }
        `}</style>

        {/* Navigation */}
        <div className="absolute top-4 left-4">
          <button
            onClick={onNavigateBack}
            className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-full p-3 hover:bg-black/60 transition-all"
          >
            <ArrowRight className="w-5 h-5 text-white transform rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomOfMirrors;